import React from 'react';
import { observer } from 'mobx-react';
import short from 'short-uuid';
import Error from 'next/error';
import { stores } from '@studionand/ada-next/src/stores';
import withStores from '@studionand/ada-next/src/stores/withStores';
import Router, { useRouter } from 'next/router';
import useStores from '../utils/useStores';
import { CONTEXT_PAGE_LABELS } from '../config';
import PageMeta from '@studionand/ada-next/src/components/PageMeta';
import getLoginFormComponent from '../utils/getLoginFormComponent';
import getExternalUrl from '../utils/getExternalUrl';
import useDynamicPageChildren from '../utils/useDynamicPageChildren';
import { getRouteConfigByUuid } from '../namedRoutes';
import getRouteConfiguration from '../utils/getRouteConfiguration';
const shortUuidTranslator = short();

export function extractUUIDfromSlug(slug) {
  const splittedSlug = slug.split('-');
  const shortUUID = splittedSlug[splittedSlug.length - 1];
  return shortUuidTranslator.toUUID(shortUUID);
}

const createLink = (context, link) => {
  const linkContext =
    process.env.NODE_ENV === 'production' ? '/' : `/${context}/`;
  return `${linkContext}${link}`;
};

const getInitialProps = (config, context) => async ({
  res,
  query,
  adaStores: { appStore },
}) => {
  appStore.setPageIsEditable(true);
  const slug = query.uuid;
  const uuidRegEx = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}/i;
  const slugIsUuid = uuidRegEx.test(slug);
  const uuid = slugIsUuid ? slug : extractUUIDfromSlug(slug);
  const hasNamedRoute = getRouteConfigByUuid(config, uuid);
  if (hasNamedRoute) {
    const Location = createLink(context, hasNamedRoute.link);
    if (res) {
      res.writeHead(307, { Location });
      res.end();
      return;
    } else {
      Router.replace(Location);
      return;
    }
  }
  const response = await stores.pagesStore.instance.loadPage(uuid, true);
  stores.appStore.instance.setEditMode(false);
  return {
    uuid,
    errorCode: response.status > 200 ? response.status : null,
  };
};

const DynamicPage = props => {
  const {
    loginStore,
    pagesStore,
    appStore,
    uuid,
    errorCode,
    layoutComponent: Layout,
    editorComponent: Editor,
  } = props;
  const { uiStore, dataStore } = useStores();
  const { context } = uiStore;
  const router = useRouter();
  const config = getRouteConfiguration(uiStore.context);
  const [triedLogin, setTriedLogin] = React.useState(false);
  const handleSetTriedLogin = () => {
    setTriedLogin(true);
  };
  const handleGetInitialProps = async () => {
    return await getInitialProps(
      config,
      context,
    )({
      dataStore,
      uiStore,
      adaStores: {
        appStore,
      },
      query: router.query,
    });
  };
  const externalUrl = getExternalUrl(context);
  const page = pagesStore.getPage(uuid);
  const LoginForm = getLoginFormComponent(context);
  const dynamicPageChildren = useDynamicPageChildren(context, uuid);
  return (
    <Layout>
      <React.Fragment>
        {(!errorCode || (triedLogin && page)) && (
          <React.Fragment>
            <PageMeta
              page={page}
              titlePrefix={`${CONTEXT_PAGE_LABELS[context]} - `}
              assetPath={`${externalUrl}/ada-assets`}
            />
            <Editor page={page} />
            {dynamicPageChildren}
          </React.Fragment>
        )}
        {errorCode && (triedLogin || loginStore.isLoggedIn) && (
          <Error statusCode={errorCode} />
        )}
        {errorCode && !triedLogin && !loginStore.isLoggedIn && (
          <LoginForm
            getInitialProps={handleGetInitialProps}
            afterSubmit={handleSetTriedLogin}
          />
        )}
      </React.Fragment>
    </Layout>
  );
};

DynamicPage.getInitialProps = getInitialProps;

export default withStores(
  'pagesStore',
  'loginStore',
  'appStore',
)(observer(DynamicPage));
